import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UI, btnSx } from "../components/ActionDrawer";
import DashTable, { Oid, PageHead, Sub2, Tag } from "../components/DashTable";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import apiService from "../Services/api.service";

const shortDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const PAGE_SIZE = 15;

/* OA9 output: AWB numbers published to the customer's order tab. */
const Shipments = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("token");

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [page, setPage] = useState(1);

  const fetchRows = async (targetPage = page) => {
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const res = await apiService.fetchShipments(token, {
        page: targetPage,
        limit: PAGE_SIZE,
      });
      setRows(res.data || []);
      setSummary(res.summary || null);
      setPagination(res.pagination || null);
    } catch (error) {
      setLoadFailed(true);
      toast.error("Error while fetching shipments");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRows(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const columns = [
    { label: "Clinic" },
    { label: "City" },
    { label: "Courier" },
    { label: "AWB Number" },
    { label: "Dispatched" },
    { label: "Expected" },
    { label: "Status" },
  ];

  const tableRows = rows.map((row) => ({
    id: row.orderId,
    cells: [
      <>
        {row.clinicName || "—"}
        <Sub2>{row.doctorName}</Sub2>
      </>,
      row.city,
      row.courier || "—",
      row.trackingId ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
          <Oid>{row.trackingId}</Oid>
          {row.trackingUrl && (
            <Box
              component="a"
              href={row.trackingUrl}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              sx={{ fontSize: 12, color: UI.p, textDecoration: "none" }}
            >
              Track
            </Box>
          )}
        </Box>
      ) : (
        <Oid>—</Oid>
      ),
      shortDate(row.dispatchedAt),
      shortDate(row.expectedDelivery),
      row.stage === "Activated" ? (
        <Tag tone="g">Activated</Tag>
      ) : row.stage === "Installation" ? (
        <Tag tone="s">Installing</Tag>
      ) : row.technicianName ? (
        <Tag tone="bl">In transit</Tag>
      ) : (
        <Tag tone="b">Awaiting technician</Tag>
      ),
    ],
  }));

  const kpis = summary
    ? [
        { label: "Shipped", value: summary.shipped },
        {
          label: "Awaiting technician",
          value: summary.awaitingTechnician,
          alarm: true,
        },
        { label: "Installing", value: summary.installing },
        { label: "Activated", value: summary.activated },
      ]
    : [];

  return (
    <Box sx={{ padding: { xs: "18px 16px 50px", md: "24px 28px 60px" }, maxWidth: 1240 }}>
      <PageHead
        title="Shipments"
        lede="Everything dispatched, with the AWB number the customer can see in Engage."
        kpis={kpis}
      />

      <DashTable
        columns={columns}
        rows={tableRows}
        onRowClick={(id) => navigate(`/dashboard/orders/${id}`)}
        emptyText="Nothing shipped yet."
        errorText={loadFailed ? "Could not load shipments." : null}
        onRetry={() => fetchRows(page)}
        foot={
          summary
            ? `${summary.shipped ?? rows.length} shipments · ${
                summary.awaitingTechnician ?? 0
              } awaiting a technician`
            : null
        }
      />

      {pagination && pagination.total > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
            marginBottom: "10px",
            fontSize: 13,
            color: UI.ink3,
          }}
        >
          <span>
            {(pagination.page - 1) * pagination.limit + 1}–
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
            {pagination.total}
          </span>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Box sx={{ display: "flex", gap: "8px" }}>
              <Box
                component="button"
                type="button"
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                disabled={pagination.page <= 1}
                sx={btnSx("ghost", pagination.page <= 1)}
              >
                Previous
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => setPage((p) => Math.min(p + 1, pagination.totalPages))}
                disabled={pagination.page >= pagination.totalPages}
                sx={btnSx("ghost", pagination.page >= pagination.totalPages)}
              >
                Next
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {isLoading && <PageLoader />}
    </Box>
  );
};

export default Shipments;
